import useVuelidate from '@vuelidate/core';
import { required, email, minLength, maxLength, numeric, helpers } from '@vuelidate/validators';
import { useLeadStore } from '@/store/LeadStore';
import { useInterfaceStore } from '@/store/UiStore';
import { inject, ref, computed } from 'vue';

import { requireIfField, requireIfFields, requireIfLeadField } from '@/helpers/ValidatorRequireIfField';
import { array } from '@/helpers/ValidatorArray';
import { get } from 'lodash';
import { GetEndPaths } from '@/helpers/GetEndPaths';
import { MunicipalitySubsidyService } from '@/services/MunicipalitySubsidyService';

export const injectGlobalVuelidate = () => {
    return inject('leadv$')
}

export const useGlobalVuelidate = () => {
    return globalVuelidateService.ref
}

export const globalVuelidateService = {
    initialized: false,
    state: null,
    v$: null,
    rules: computed(() => {
        // let state = globalVuelidateService.state;
        return {
          meta: {
            PartnerCoupon: { required },
          },
          bellerLoper: {
            Naam: { required },
          },
          interests: {
            Interests: {
              required,
              minLength: minLength(1),
              array,
            },
          },
          currentInsulation: {
            FloorInsulated: { required },
            Participate: {
              required: requireIfFields({
                FloorInsulated: ['Nee', 'Weet ik niet'],
                Eligible_ms: [true]
              }),
            },
            FloorInsulationImpossible: {
              required: requireIfField('FloorInsulated', 'Niet mogelijk'),
            },
            InterestedInFutureCampaign: {
              required: requireIfFields({
                FloorInsulated: ['Niet mogelijk', 'Ja']
              }),
            },
            InterestedInIsolatiePlan: {
              required: requireIfFields({
                FloorInsulated: ['Niet mogelijk', 'Ja']
              }),
            },
            ConfirmNoParticipation: {
              // required if Participate is false
              required: requireIfField('Participate', false),
              // must be false if required
              notTrue: (value, form) => {
                if (form.Participate === false) {
                  return value;
                }
                return true;
              },
            },
          },
          contact: {
            Salutation: {
              required: helpers.withMessage('Kies een aanhef.', (value) => {
                const uiStore = useInterfaceStore();
                return uiStore.isExistingCustomer || helpers.req(value);
              }),
            },
            FirstName: { required },
            LastName: { required },
            Email: { required, email },
            Phone: {
              phone: (value) => {
                value = String(value).replaceAll(' ', ''); // remove whitespace
                value = String(value).replaceAll('-', ''); // remove dashes
                return value.length >= 8 // at least 8 chars long
                  && !/[^\d\s\-+]/.test(value) // only allow - + space and numbers
                  && (value.match(/\d/g) || []).length > 7 // minimum 8 numbers
                  && value.length <= 15; // max 15 chars
              }
            },
          },
          address: {
            StreetNumber: { required },
            PostalCode: { 
              zipCode: (value) => {
                value = String(value).replaceAll(' ', ''); // remove whitespace
                return value.length == 6 // 6 chars
                  && (value.match(/\d/g) || []).length == 4; // 4 numbers
              }
             },
            Street: { },
            City: { }
          },
          registration: {
            WantsToRegisterAnyway: { },
            RegisterAnywayReason: {
              required: requireIfField('WantsToRegisterAnyway', true),
              maxLength: maxLength(180),
            }
          },
          ownership: {
            PropertyOwner: {
              required,
            },
            OwnerPermission: {
              required: requireIfField('PropertyOwner', false),
            },
            OwnerName: {
              required: requireIfFields({
                PropertyOwner: false,
                OwnerPermission: true,
              }),
            },
            OwnerPhone: {
              required: requireIfFields({
                PropertyOwner: false,
                OwnerPermission: true,
              }),
            },
            OwnerEmail: {
              required: requireIfFields({
                PropertyOwner: false,
                OwnerPermission: true,
              }),
              email,
            },

            HoaMember: { 
              required: (value) => {
                const lead = useLeadStore();
                // If lead is from d2d.bureauverduurzamen.nl, skip validation
                if (lead.meta.LeadSource?.includes('d2d.bureauverduurzamen.nl')) {
                  return true;
                }
                return helpers.req(value);
              }
             },
            HoaPermission: {
              required: requireIfField('HoaMember', true),
            },
            HoaRepName: {
              required: requireIfFields({
                HoaMember: true,
                HoaPermission: true,
              }),
            },
            HoaRepPhone: {
              required: requireIfFields({
                HoaMember: true,
                HoaPermission: true,
              }),
            },
            HoaRepEmail: {
              required: requireIfFields({
                HoaMember: true,
                HoaPermission: true,
              }),
              email,
            },
          },
          contactPreferences: {
            PreferredContactTime: { required },
            SpeaksDutch: { required },
            PreferredOtherLanguage: { 
              required: requireIfField('SpeaksDutch', false)
            }
          },
          property: {
            // always required and must be 4 digits
            ConstructionYear: { required, minLength: minLength(4), maxLength: maxLength(4), numeric },
            // GasConsumption: {},
            // PowerConsumption: {},
            HouseType: { required },
            IsMonument: { required },
            IsBeschermdGezicht: { required },
            InVersterkingsprogramma: {
              required: requireIfLeadField('municipalityHasNoVersterking', false),
            },
          },
          financing: {
            income: {
              TooHighIncomeShortcut: { required },
              ReceivedEnergySupport: { },
              RequireLeefVorm: { },
              CivilStatus: { required: requireIfLeadField('TooHighIncomeShortcut', false) },
              SM_140pct: { required },
            },
            nip: {
              OverMaxIncome: { required },
              // required if lead.NIP_potentially_eligible = true
              PermissionToRequestNip: { required: requireIfLeadField('NIP_potentially_eligible', true) },
            },

            snnSubsidyVenV: {
              hulpSnnSubsidies: { required },
              
              RequestedSnn10k: {
                required: requireIfFields({
                  hulpSnnSubsidies: true,
                  InVersterking: 'Nee'
                })
              },
              
              snnSubsidy4k: {
                HeeftAardbevingsschade: {
                  required: requireIfLeadField(
                    'financing.snnSubsidyVenV.hulpSnnSubsidies',
                    true
                  )
                },

                NietEerderGebruikt: {
                  required: (value) => {
                    const lead = useLeadStore();

                    // Alleen required als hulpSnnSubsidies = true EN HeeftAardbevingsschade = true
                    if (
                      lead.financing?.snnSubsidyVenV?.hulpSnnSubsidies === true &&
                      lead.financing?.snnSubsidyVenV?.snnSubsidy4k?.HeeftAardbevingsschade === true
                    ) {
                      return helpers.req(value);
                    }
                    // Niet required = altijd valid (als vraag niet getoond wordt)
                    return true;
                  }
                }
              },
            },

            wantHelpFinancing: { required }
          },

          twk: {
            IntakeMeasuresExecuted: { required },
          },

          isolatieplanCheck: {
            Under10kCombined: {
              require: (value) => {
                let lead = useLeadStore();

                if (lead.Isolatieplan_required) {
                  return true;
                }

                return lead.twk.IntakeMeasuresExecuted === true ? helpers.req(value) : true;
              },
            },
            ProceedWithoutPlan: {
              require: (value) => {
                let lead = useLeadStore();

                if (lead.Isolatieplan_required) {
                  return true;
                }

                return lead.twk.IntakeMeasuresExecuted === false ? helpers.req(value) : true;
              }
            },
            // IsolatieplanPlus: {
            //   required: (value) => {
            //     const lead = useLeadStore();

            //     if (lead.Isolatieplan_required_bouwjaar || lead.property.IsMonument) {
            //       return true;
            //     }

            //     return lead.address.eligible_for_isoplan_plus === true ? helpers.req(value) : true;
            //   },
            // },
            IsolatieplanRequested: {
              required: (value) => {
                const lead = useLeadStore();
                const relevant = lead.wantsIsolatieplan === true;
                return relevant ? helpers.req(value) : true;
              },
            },

            residentChoosesOwnAdvisor: {
              required: (value) => {
                const lead = useLeadStore();
                return lead.isolatieplanCheck.IsolatieplanRequested === false
                  ? helpers.req(value)
                  : true;
              },
            },
            IsolatieplanReceived: {
              required: (value) => {
                const lead = useLeadStore();
                return lead.isolatieplanCheck.IsolatieplanRequested === true ? helpers.req(value) : true;
              },
            },
          },
          intakeCompletion: {
            UserSituation: {required},
          },

          advice: {
            Maatregel29Support: {required},
          },

          //The two fields below can be empty
          executedMeasures: {
          },
          wishes: {
          },

          isolatieplan: {
            DeclaredAtOtherSubsidy: {
              required: (value) => {
                const lead = useLeadStore();
                return lead.twk.IntakeMeasuresExecuted === true ? helpers.req(value) : true;
              }
            },
            // RetroactiveMeasuresTotalAmount: {
            //   required: requireIfFields({
            //     HasMeasuresForRetroactive: true,
            //     DeclaredAtOtherSubsidy: false
            //   }),
            // },
            // RetroactiveMeasures: {
            //   required: requireIfField('HasMeasuresForRetroactive', true),
            // },
          },
          otherSubsidies: {
            energyLabelDOrLower: {
              required: (value) => {
                const lead = useLeadStore();
                const municipality = lead.address?.pc_GemNaam;
                const municipalities = [
                  "Pekela",
                  "Midden-Groningen",
                  "Veendam",
                  "Stadskanaal",
                  "Oldambt",
                  "Westerwolde",
                ];

                if (!municipalities.includes(municipality)) {
                  return true;
                }

                return helpers.req(value);
              },
            },
            hasTwoPoorInsulatedParts: {
              required: (value) => {
                const lead = useLeadStore();
                const municipality = lead.address?.pc_GemNaam;
                const municipalities = [
                  "Pekela",
                  "Midden-Groningen",
                  "Veendam",
                  "Stadskanaal",
                  "Oldambt",
                  "Westerwolde",
                ];

                if (!municipalities.includes(municipality)) {
                  return true;
                }

                if (
                  lead.otherSubsidies?.energyLabelDOrLower !== false &&
                  lead.otherSubsidies?.energyLabelDOrLower !== "unknown"
                ) {
                  return true;
                }

                return helpers.req(value);
              },
            },
            energyLabelDOrLowerOrTwoPoorInsulatedParts: {
              required: (value) => {
                const lead = useLeadStore();
                const municipality = lead.address?.pc_GemNaam;
                const municipalitiesUsingSplitQuestion = [
                  "Pekela",
                  "Midden-Groningen",
                  "Veendam",
                  "Stadskanaal",
                  "Oldambt",
                  "Westerwolde",
                ];

                if (!municipalitiesUsingSplitQuestion.includes(municipality)) {
                  return true;
                }

                return helpers.req(value);
              },
            },
            vhfEnergyLabelBOrLower: {
              required: (value) => {
                const lead = useLeadStore();
                const municipality = lead.address?.pc_GemNaam;

                if (municipality !== "Veendam") {
                  return true;
                }

                if (lead.otherSubsidies?.energyLabelDOrLowerOrTwoPoorInsulatedParts !== false) {
                  return true;
                }

                return helpers.req(value);
              },
            },
            vhfNeedsIsolationStandardMeasures: {
              required: (value) => {
                const lead = useLeadStore();
                const municipality = lead.address?.pc_GemNaam;
                const municipalities = ["Midden-Groningen", "Oldambt"];

                if (!municipalities.includes(municipality)) {
                  return true;
                }

                return helpers.req(value);
              },
            },
            vhf: {
              measuresExecuted: {
                required: (value) => {
                  if (!MunicipalitySubsidyService.executionChecksEnabled) return true;
                  const lead = useLeadStore();
                  const municipality = lead.address?.pc_GemNaam;
                  const municipalities = ["Pekela", "Midden-Groningen", "Veendam", "Oldambt"];
                  return municipalities.includes(municipality) ? helpers.req(value) : true;
                },
              },
              executedWithinRequiredPeriod: {
                required: (value) => {
                  if (!MunicipalitySubsidyService.executionChecksEnabled) return true;
                  const lead = useLeadStore();
                  const municipality = lead.address?.pc_GemNaam;
                  const municipalities = ["Pekela", "Midden-Groningen"];
                  if (!municipalities.includes(municipality)) {
                    return true;
                  }

                  if (lead.otherSubsidies?.vhf?.measuresExecuted !== true) {
                    return true;
                  }

                  return helpers.req(value);
                },
              },
            },
            subsidy: {
              measuresExecuted: {
                required: (value) => {
                  if (!MunicipalitySubsidyService.executionChecksEnabled) return true;
                  const lead = useLeadStore();
                  const subsidies = MunicipalitySubsidyService.getEligibleSubsidies(lead, "financingVhf");
                  return subsidies?.length ? helpers.req(value) : true;
                },
              },
              executedAt: {
                required: (value) => {
                  if (!MunicipalitySubsidyService.executionChecksEnabled) return true;
                  const lead = useLeadStore();
                  const subsidies = MunicipalitySubsidyService.getEligibleSubsidies(lead, "financingVhf");
                  const hasTwkRule = subsidies.some((subsidy) =>
                    MunicipalitySubsidyService.getExecutionConfig(subsidy, lead)?.twkFrom,
                  );
                  if (!hasTwkRule || lead.otherSubsidies?.subsidy?.measuresExecuted !== true) {
                    return true;
                  }

                  return helpers.req(value);
                },
              },
            },
            woz: {
              woz_waarde_volgens_bewoner: { required },
            },
          
          },
          intentions: {
            Termijn: { required },
            Aanpak: { },
          },
          cavity_wall: {
            general: {
              CavityWallPresent: { required },
              FacadeMaterial: { required },
            },
            paint: {
              WallsPainted: { required },
              AcceptWallRepaint: {
                require: requireIfField('WallsPainted', true),
              },
            },
            stucco: {
              WallsPlastered: { required },
              AcceptStuccoRepaint: {
                require: requireIfField('WallsPlastered', true),
              },
            },
          },
          floor: {
            general: {
              SpaceBeneathFloor: { required },
              CrawlSpaceAccessible: { required },
            },
            details: {
              CrawlSpaceHeightCm: { required },
              FloorInsulationSide: {  },
            },
          },
          roof: {
            general: {
              RoofInsulationStatus: { required },
              RoofInsulationSide: { required },
              RoofType: { required },
            },
            attic: {
              AtticPresent: { required },
              AccessToRoofInside: { required },
              AtticUsage: {
                // required if skipRoofFinishing = false
                require: (value) => {
                  let lead = useLeadStore();
                  return !lead.skipRoofFinishing ? helpers.req(value) : true;
                },
              },
            },
            finishing: {
              RoofBeamsVisible: {
                // required if skipRoofFinishing = false
                require: (value) => {
                  let lead = useLeadStore();
                  return !lead.skipRoofFinishing ? helpers.req(value) : true;
                },
              },
              RoofInsideFinishingPresent: {
                // required if skipRoofFinishing = false AND attic is used for living
                require: (value) => {
                  let lead = useLeadStore();
                  return !lead.skipRoofFinishing && lead.AtticUsedForLiving
                    ? helpers.req(value)
                    : true; // not required = always valid
                },
              },
              RoofFinishingRemovalPermission: {
                // required if skipRoofFinishing = false AND attic is used for living
                require: (value, form) => {
                  let lead = useLeadStore();
                  return !lead.skipRoofFinishing && lead.AtticUsedForLiving && form.RoofInsideFinishingPresent == true
                    ? helpers.req(value)
                    : true; // not required = always valid
                },
              },
              RoofBeamCoveringRemoved: {
                // required if skipRoofFinishing = false AND attic is used for living AND roof finishing is present
                require: (value, form) => {
                  let lead = useLeadStore();
                  return !lead.skipRoofFinishing &&
                    lead.AtticUsedForLiving &&
                    form.RoofInsideFinishingPresent == true
                    ? helpers.req(value)
                    : true; // not required = always valid
                },
              },
            },
          },
          glazing: {
            general: {
              IsMonument: { required },
              BuildingAccessibleAllround: { required },
              CurrentGlazingType: { required },
              CurrentGlazingAge: { required }
            },
            frame: {
              FrameMaterial: { required },
              FrameCondition: {
                require: requireIfField('FrameMaterial', 'Hout')
              },
              RebateWidthSufficient: { required }
            },
            dimensions: {
              // Panes is not required
            },
          },
          heatpump: {
            HeatingSystem: { required },
            HeatpumpFinancing: { required }
          },
          solar: {
            PlacementLocations: {
              required,
              minLength: minLength(1),
              array,
            },
          }
        };
    }),
    init() {
        this.state = useLeadStore();
        this.v$ = useVuelidate(this.rules, this.state);
        this.ref = ref(this.v$)
    }
};

export let globalVuelidatePlugin = {
  install: (app) => {
    globalVuelidateService.init();
    app.provide('leadv$', ref(globalVuelidateService.v$));
  },
};

export const touchAllWithValue = function()
{
  let lead = useLeadStore();
  let v$ = globalVuelidateService.ref;
  let paths = GetEndPaths(lead.$fields);
  for (let i = 0; i < paths.length; i++) {
    const path = paths[i];
    let value = get(lead, path, null);
    if (value !== null) {
      let fv$ = get(v$.value, path, null);
      if (fv$ !== null) {
        fv$.$touch();
      }
    }
  }
};
